import { securePost, secureGet, secureDelete } from "../../Config/ApiConfig";
import { removeNullUndefinedWithReduce } from "../../Utils/javascript";
import { deleteConversation } from "../Messages/MessagesServices";

export const getVirtualContacts = async (agentId) => {
    try {
        const filters = removeNullUndefinedWithReduce({ is_virtual: true });
        const { data } = await secureGet(
            `/agents/${agentId}/contacts?page=1&page_size=5&sort_by=created_at&sort_direction=desc&filters=${encodeURIComponent(JSON.stringify(filters))}`
        );

        // Return virtual contacts if they exist
        if (data.data && data.data.length > 0) {
            return data.data;
        }
        return [];
    } catch (error) {
        throw new Error(`Failed to fetch virtual contacts: ${error.message}`);
    }
};

export const createVirtualContact = async (agentId, virtualContactData = {}) => {
    try {
        const defaultContactData = {
            first_name: "Test",
            last_name: "Customer",
            email: "test.customer@example.com",
            phone: "1234567890",
            locale: "en_US",
            timezone: -new Date().getTimezoneOffset() / 60,
            gender: "male",
            note: "Virtual test customer created for agent testing",
            ...virtualContactData
        };

        const { data } = await securePost(`/agents/${agentId}/virtual-contacts`, defaultContactData);
        return data?.data;
    } catch (error) {
        throw new Error(`Failed to create virtual contact: ${error.message}`);
    }
};

export const getVirtualContactConversation = async (contactId) => {
    try {
        const { data } = await secureGet(`/contacts/${contactId}/conversation`);
        return data?.data;
    } catch (error) {
        throw new Error(`Failed to get virtual contact conversation: ${error.message}`);
    }
};

export const getExistingVirtualConversation = async (agentId) => {
    try {
        const filters = removeNullUndefinedWithReduce({ is_virtual: true });
        const { data } = await secureGet(
            `/agents/${agentId}/conversations?page=1&page_size=5&sort_by=updated_time&sort_direction=desc&filters=${encodeURIComponent(JSON.stringify(filters))}`
        );

        // Return the first virtual conversation if exists
        if (data.data && data.data.length > 0) {
            return data.data[0];
        }
        return null;
    } catch (error) {
        throw new Error(`Failed to fetch virtual conversations: ${error.message}`);
    }
};

export const startAgentTestingConversation = async (agentId) => {
    try {
        // Step 1: Check if virtual contacts exist for this agent
        let virtualContacts = await getVirtualContacts(agentId);

        // Step 2: If no virtual contacts exist, create a default one
        if (virtualContacts.length === 0) {
            const newVirtualContact = await createVirtualContact(agentId);
            virtualContacts = [newVirtualContact];
        }

        // Step 3: Use the first virtual contact to get/create conversation
        const virtualContact = virtualContacts[0];

        // Step 4: Check if there's already a conversation for this virtual contact
        let conversation;
        try {
            conversation = await getVirtualContactConversation(virtualContact.id);
        } catch (error) {
            // If conversation doesn't exist, it will be created by the backend
            conversation = await getVirtualContactConversation(virtualContact.id);
        }

        return conversation;
    } catch (error) {
        throw new Error(`Failed to start test conversation: ${error.message}`);
    }
};

export const deleteAgentTestingConversation = async (agentId) => {
    try {
        const existing = await getExistingVirtualConversation(agentId);
        if (existing?.id) {
            await deleteConversation(existing.id);
            return true;
        }
        return false;
    } catch (error) {
        throw new Error(`Failed to delete test conversation: ${error.message}`);
    }
};

export const sendAgentTestingMessage = async (virtualContactId, messageData) => {
    try {
        const { data } = await securePost(
            `/contacts/${virtualContactId}/messages`,
            messageData
        );
        return data;
    } catch (error) {
        throw new Error(`Failed to send message to virtual contact: ${error.message}`);
    }
};

export const deleteVirtualContact = async (contactId) => {
    try {
        const { data } = await secureDelete(`/contacts/${contactId}`);
        return data;
    } catch (error) {
        throw new Error(`Failed to delete virtual contact: ${error.message}`);
    }
};